/**
 * @file Sortable系コンポーネントの共通フック
 */

import type { DragEndEvent, DragStartEvent } from "@dnd-kit/core";
import {
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { useLingui } from "@lingui/react/macro";
import { useCallback, useId, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { useSidebarGroupOrder } from "../../../_contexts/sidebar-group-order-context";

import {
  getApiType,
  handleDragEndCommon,
  reorderByIds,
} from "./sortable-utils";
import type { BaseProject, SidebarItemType } from "./types";

interface UseSortableItemsOptions<T extends BaseProject> {
  workspaceId: string;
  items: T[];
  sidebarItemType: SidebarItemType;
  updateAction?: (ids: string[], type: string) => Promise<{ error?: Error }>;
}

interface UseSortableItemsReturn<T extends BaseProject> {
  orderedItems: T[];
  isUpdating: boolean;
  activeId: string | null;
  sensors: ReturnType<typeof useSensors>;
  handleDragStart: (event: DragStartEvent) => void;
  handleDragEnd: (event: DragEndEvent) => void;
  handleDragCancel: () => void;
  dndContextId: string;
}

/**
 * Sortableアイテムの共通ロジックを提供するフック
 */
export function useSortableItems<T extends BaseProject>({
  workspaceId,
  items,
  sidebarItemType,
  updateAction,
}: UseSortableItemsOptions<T>): UseSortableItemsReturn<T> {
  const { t } = useLingui();

  // デフォルトのエラーハンドラー
  const handleError = useCallback(
    (message: string) => toast.error(message),
    [],
  );
  const { getSidebarGroupOrder, setSidebarGroupOrder } = useSidebarGroupOrder();

  // 状態管理
  const [isUpdating, setIsUpdating] = useState(false);
  const [activeId, setActiveId] = useState<string | null>(null);
  const dragEndTimeRef = useRef<number>(0);
  const uniqueId = useId();
  const dndContextId = `sortable-${sidebarItemType}-${uniqueId}`;

  // センサー設定（ビューレベルと同じ設定に統一）
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 10, // 10px移動してからドラッグ開始
      },
    }),
    useSensor(KeyboardSensor),
  );

  // 順序取得と並び替え
  const storedOrder = getSidebarGroupOrder(workspaceId, sidebarItemType);
  const orderedItems = useMemo(() => {
    return reorderByIds(items, storedOrder);
  }, [items, storedOrder]);

  // ドラッグ開始ハンドラー
  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveId(String(event.active.id));
  }, []);

  // ドラッグ終了ハンドラー
  const handleDragEnd = useCallback(
    async (event: DragEndEvent) => {
      const now = Date.now();
      if (now - dragEndTimeRef.current < 100) {
        return;
      }
      dragEndTimeRef.current = now;

      setActiveId(null);

      const result = handleDragEndCommon(event, orderedItems);
      if (!result) return;

      const { reorderedIds } = result;

      // 楽観的更新
      setSidebarGroupOrder(workspaceId, reorderedIds, sidebarItemType);
      setIsUpdating(true);

      try {
        if (updateAction) {
          const apiType = getApiType(sidebarItemType);
          const actionResult = await updateAction(reorderedIds, apiType);

          if (actionResult.error) {
            // エラーの場合は元に戻す
            const originalOrder = orderedItems.map((item) => item.id);
            setSidebarGroupOrder(workspaceId, originalOrder, sidebarItemType);
            handleError(t`Failed to update ${sidebarItemType} order`);
          }
        }
      } catch (_error) {
        // エラーの場合は元に戻す
        const originalOrder = orderedItems.map((item) => item.id);
        setSidebarGroupOrder(workspaceId, originalOrder, sidebarItemType);
        handleError(t`Failed to update ${sidebarItemType} order`);
      } finally {
        setIsUpdating(false);
      }
    },
    [
      orderedItems,
      workspaceId,
      sidebarItemType,
      updateAction,
      setSidebarGroupOrder,
      handleError,
      t,
    ],
  );

  // ドラッグキャンセルハンドラー
  const handleDragCancel = useCallback(() => {
    setActiveId(null);
  }, []);

  return {
    orderedItems,
    isUpdating,
    activeId,
    sensors,
    handleDragStart,
    handleDragEnd,
    handleDragCancel,
    dndContextId,
  };
}
